import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { DiscountButton } from '@euler/app/flows/task/pages/quotation/components/quotation-form/DiscountButton';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { QuotationFormStaged } from '@euler/model/task-detail';
import { memo, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export const QuotationFormFooter = memo(
  ({
    form,
    dirty,
    editable,
    readOnly,
    onDiscountChange,
    onSave,
    onConfirm,
    onRequestAmend,
    onPrint,
  }: {
    form: QuotationFormStaged;
    dirty?: boolean;
    editable?: boolean;
    readOnly?: boolean;
    onDiscountChange: (discount: number) => void;
    onSave: () => void;
    onConfirm: () => void;
    onRequestAmend: () => void;
    onPrint?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const subjectCount = form.subjects.filter(x => x.included).length;

    const priceInfo = useMemo(() => {
      let originalTotal = 0;
      let discountedTotal = 0;
      let computedTotal = 0;
      let totalDiscount = 0;
      let originalManHourCostTotal = 0;
      let originalMaterialTotal = 0;
      let discountedManHourCostTotal = 0;
      let discountedMaterialTotal = 0;

      for (const subject of form.subjects) {
        if (!subject.included) continue;

        let itemCount = 0;

        for (const item of subject.items) {
          if (!item.included) continue;

          if (item.price == null) {
            return null;
          }

          itemCount++;

          originalTotal += item.price;
          discountedTotal += item.discountPrice ?? item.price;
          originalManHourCostTotal += item.price;
          discountedManHourCostTotal += item.discountPrice ?? item.price;
          if (item.discountPrice != null && item.discountPrice <= item.price) {
            totalDiscount += item.price - item.discountPrice;
          }
          for (const material of item.materials) {
            if (!material.included) continue;
            if (material.price == null) {
              return null;
            }
            const qty = material.qty ?? 1;
            originalTotal += material.price * qty;
            discountedTotal += (material.discountPrice ?? material.price) * qty;
            originalMaterialTotal += material.price * qty;
            discountedMaterialTotal +=
              (material.discountPrice ?? material.price) * qty;
            if (
              material.discountPrice != null &&
              material.price != null &&
              material.discountPrice <= material.price
            ) {
              totalDiscount += (material.price - material.discountPrice) * qty;
            }
          }
        }

        if (itemCount === 0) {
          return null;
        }

        if (subject.additionalManHourCostSubTotalDiscount != null) {
          discountedTotal -= subject.additionalManHourCostSubTotalDiscount;
          totalDiscount += subject.additionalManHourCostSubTotalDiscount;
          discountedManHourCostTotal -=
            subject.additionalManHourCostSubTotalDiscount;
        }

        if (subject.additionalMaterialSubTotalDiscount != null) {
          discountedTotal -= subject.additionalMaterialSubTotalDiscount;
          totalDiscount += subject.additionalMaterialSubTotalDiscount;
          discountedMaterialTotal -= subject.additionalMaterialSubTotalDiscount;
        }

        if (subject.additionalSubTotalDiscount != null) {
          discountedTotal -= subject.additionalSubTotalDiscount;
          totalDiscount += subject.additionalSubTotalDiscount;
        }
      }

      computedTotal = discountedTotal;

      if (form.additionalTotalFeeDiscount != null) {
        computedTotal -= form.additionalTotalFeeDiscount;
        totalDiscount += form.additionalTotalFeeDiscount;
      }

      return {
        computedTotal,
        originalTotal,
        discountedTotal,
        totalDiscount,
        originalManHourCostTotal,
        originalMaterialTotal,
        discountedManHourCostTotal,
        discountedMaterialTotal,
      };
    }, [form.additionalTotalFeeDiscount, form.subjects]);

    const isConfirmDisabled = priceInfo == null || !subjectCount;

    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={styles.container}
        css={`
          box-shadow: 0 0 ${rpx(15)}px rgba(0, 0, 0, 0.25);
        `}
      >
        <StackLayout>
          <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
            <Typography.Text
              style={styles.totalLabel}
            >{`已选${subjectCount}项, 合计:`}</Typography.Text>
            <Typography.Text style={styles.total}>{`￥${
              priceInfo == null ? '--' : priceInfo.computedTotal.toFixed(2)
            }`}</Typography.Text>
            {priceInfo != null && (
              <DiscountButton
                label="总价"
                editable={editable}
                price={priceInfo.originalTotal}
                discountPrice={priceInfo.discountedTotal}
                discount={form.additionalTotalFeeDiscount}
                onDiscountChange={onDiscountChange}
              />
            )}
          </StackLayout>
          <StackLayout direction="row" alignItems="center">
            <Typography.Text
              style={styles.totalDiscountLabel}
            >{`优惠金额: `}</Typography.Text>
            <Typography.Text style={styles.totalDiscount}>{`￥${
              priceInfo == null ? '--' : priceInfo.totalDiscount.toFixed(2)
            }`}</Typography.Text>
          </StackLayout>
        </StackLayout>
        <StackLayout direction="row" gap={0}>
          {editable && form.subjects.length > 0 ? (
            <Center.Touchable
              hitSlop={PredefinedHitSlops.large}
              onPress={dirty ? onSave : onPrint}
              css={`
                padding: 0 ${rpx(10)}px;
              `}
            >
              <Typography.Text medium>
                {dirty ? '保存' : '打印'}
              </Typography.Text>
            </Center.Touchable>
          ) : form.subjects.length > 0 ? (
            <Center.Touchable
              hitSlop={PredefinedHitSlops.large}
              onPress={onPrint}
              css={`
                padding: 0 ${rpx(10)}px;
              `}
            >
              <Typography.Text medium>打印</Typography.Text>
            </Center.Touchable>
          ) : null}
          {editable ? (
            <Center.Touchable
              disabled={isConfirmDisabled}
              onPress={onConfirm}
              css={`
                background-color: ${isConfirmDisabled
                  ? '#bbb'
                  : StatusColors.Info};
                height: ${rpx(44)}px;
                border-radius: ${rpx(44)}px;
                padding: 0 ${rpx(20)}px;
              `}
            >
              <Typography.Text color="#fff" bold size={rpx(18)}>
                确认报价
              </Typography.Text>
            </Center.Touchable>
          ) : (
            <Center.Touchable
              disabled={readOnly}
              onPress={onRequestAmend}
              css={`
                background-color: ${priceInfo == null
                  ? '#bbb'
                  : StatusColors.Info};
                height: ${rpx(44)}px;
                border-radius: ${rpx(44)}px;
                padding: 0 ${rpx(20)}px;
              `}
            >
              <Typography.Text color="#fff" bold size={rpx(18)}>
                修改报价单
              </Typography.Text>
            </Center.Touchable>
          )}
        </StackLayout>
      </StackLayout>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();
  return useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: '#f9f9f9',
          paddingHorizontal: rpx(15),
          paddingBottom: Math.max(rpx(15), bottom * 0.75 + rpx(4)),
          paddingTop: rpx(15),
        },
        totalLabel: {
          fontSize: rpx(12),
          color: '#333',
        },
        total: {
          color: StatusColors.Danger,
          fontSize: rpx(23),
          fontFamily: FontFamily.NotoSans.Black,
          top: rpx(-2),
          fontWeight: '800',
          letterSpacing: rpx(-1.5),
        },
        totalDiscountLabel: {
          color: '#333',
          fontSize: rpx(12),
        },
        totalDiscount: {
          fontSize: rpx(12),
          color: StatusColors.Danger,
        },
      }),
    [bottom, rpx],
  );
};
